import { Button, CircularProgress, Dialog, InputBase, Snackbar } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ChangeOrgProfile from "../reusables/ChangeOrgProfile";
import { clearTokens, setTokensFromResponse } from "../../utils/authTokens";
import axios from "axios";
import { Phone, PhoneCall, X } from "lucide-react";
import DeleteUserAccountModal from "./DeleteUserAccountModal";

const ChangeOrgModal = ({ open, setOpen, user, orgs }) => {
  const navigate = useNavigate();
  const [err_msg, setErrorMsg] = useState(null);
  const [showCreateNewOrganization, setShowCreateNewOrganization] =
    useState(false);
  const [loader, setLoader] = useState(false);
  const [organizations, setOrganization] = useState(
    JSON.parse(localStorage.getItem("user"))?.relatedOrgs.L
  );
  const [successMsg, setSuccessMsg] = useState(null);

  const [user_, setUser] = useState({
    organization_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userId: JSON.parse(localStorage.getItem("user"))?.userId?.S || null,
    email: JSON.parse(localStorage.getItem("user"))?.email?.S || null,
    password: JSON.parse(localStorage.getItem("user"))?.password?.S || null,
    first_name: JSON.parse(localStorage.getItem("user"))?.fName?.S || null,
    last_name: JSON.parse(localStorage.getItem("user"))?.lName?.S || null,
    company_name:
      JSON.parse(localStorage.getItem("organization"))?.organization_name?.S ||
      "",
    agreed: false,
    registeredFromGoogle:
      JSON.parse(localStorage.getItem("user"))?.registeredFromGoogle?.BOOL ||
      false,
  });

  const addOrganization = async () => {
    setErrorMsg(null);
    if (user_.company_name.length >= 1) {
      setLoader(true);
      await axios
        .post(
          process.env.REACT_APP_BACKEND_URL + "user/add-created-user-to-org",
          {
            ...user_,
            userId: JSON.parse(localStorage.getItem("user"))?.userId?.S,
          }
        )
        .then((res) => {
          if (res.data.status === "success") {
            localStorage.setItem("user", JSON.stringify(res.data.user)); //store user data in local storage
            localStorage.setItem(
              "organization",
              JSON.stringify(res.data.organization)
            ); //stores organization datat in local storage
            setTokensFromResponse(res.data);
            setSuccessMsg(
              "Organization has been created, we would now redirect you to complete onboarding flow. "
            );
            setTimeout(() => {
              navigate("/register");
              setLoader(false);
            }, 4000);
          } else {
            setErrorMsg(`${res.data.msg}`);
            setLoader(false);
          }
        })
        .catch((err) => {
          setLoader(false);
        });
    } else {
      setErrorMsg("Please enter company name for your organization");
    }
  };

  const deleteUser = async () => {
    setErrorMsg(null);
    setLoader(true);
    let deletedUser = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "delete-user-by-id",
      {
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      }
    );

    setSuccessMsg("User deleted successfully");
    localStorage.clear();
    setTimeout(() => {
      navigate("/login");
      setLoader(false);
    }, 2000);
  };

  const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);

  const handleLogout = () => {
    console.info("[Auth] Logging out: user chose Log out from change-org modal (no longer a member of current org).");
    clearTokens();
    localStorage.removeItem("user");
    localStorage.removeItem("organization");
    setOpen(false);
    navigate("/login");
  };

  const changeOrganization = async (org_id) => {
    try {
      const { fetchAndSetOrganization } = await import("../../utils/organizationService");
      await fetchAndSetOrganization(org_id);

      let user_data = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
        {
          userId: JSON.parse(localStorage.getItem("user")).userId.S,
        }
      );
      localStorage.setItem("user", JSON.stringify(user_data.data.user));

      window.location.reload();
    } catch (error) {
      if (error.response?.data) {
        setErrorMsg(error.response.data.message);
      }
      setLoader(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      BackdropProps={{
        style: { backgroundColor: "rgba(144,150,160,0.8)" },
      }}
      PaperProps={{
        sx: {
          borderRadius: "14px",
          padding: "32px 28px",
        },
      }}
    >
      <Snackbar
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg(null)}
        message={
          successMsg
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        ContentProps={{
          sx: {
            background: "linear-gradient(45deg, #333, #444)",
            color: "#fff",
            fontWeight: "bold",
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
            padding: "8px 16px",
            animation: "slideIn 0.3s ease-out",
          },
        }}
      />
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
        `}
      </style>
      <DeleteUserAccountModal
        loader={loader}
        setLoader={setLoader}
        deleteUser={deleteUser}
        open={openDeleteAccountModal}
        setOpen={setOpenDeleteAccountModal}
      />
      <div className="flex flex-col items-center text-center">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-5">
          <PhoneCall className="text-indigo-600" />
          <span className="font-semibold text-[18px] text-gray-800">
            Twilanswer
          </span>
        </div>

        {/* Alert */}
        <div className="w-full bg-gray-50 border border-gray-100 rounded-lg p-4 mb-4">
          <p className="text-[14px] font-medium text-gray-800 mb-1">
            Oops! You are not a member of this organization.
          </p>
          <p className="text-[13px] text-gray-500">
            You are no longer a member of{" "}
            <span className="font-semibold">
              {
                JSON.parse(localStorage.getItem("organization"))
                  ?.organization_name?.S
              }
            </span>{" "}
            Please create a new organization or log out.
          </p>
        </div>

        {/* Org selector (kept intact) */}
        <div className="w-full mb-4">
          <ChangeOrgProfile
            changeOrganization={changeOrganization}
            setShowCreateNewOrganization={setShowCreateNewOrganization}
          />
        </div>

        {/* CTA */}

        {organizations?.length == 0 ||
          (showCreateNewOrganization && (
            <div className="w-full">
              <div className="space-y-2 mb-5 mt-6">
                <div className="flex items-center justify-between">
                  <div className=" lg:text-[14px] text-[13px] text-left ">
                    Company name
                  </div>

                  <X
                    className="w-4 h-4 text-gray-400 cursor-pointer"
                    onClick={() => {
                      setShowCreateNewOrganization(false);
                    }}
                  />
                </div>
                <InputBase
                  value={user_.company_name}
                  onChange={(e) => {
                    setUser({ ...user, company_name: e.target.value });
                  }}
                  sx={{
                    bgcolor: "#F7F7F8",
                    width: "100%",
                    px: 2,
                    py: "2px",
                    fontSize: "14px",
                  }}
                />
              </div>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => {
                  addOrganization();
                }}
                disabled={loader}
                sx={{
                  borderRadius: "4px",
                  py: "5px",
                  mb: "10px",
                }}
                >
                {loader ? (
                  <CircularProgress
                  size={20}
                  thickness={4}
                  sx={{
                  color: "#fff",
                  }}
                  />
                ) : (
                  "Create New Organization"
                )}
                </Button>
                </div>
                ))}
                
              {/* Footer actions */}
            <div className="flex items-center justify-between w-full mt-2 text-[13px] flex-wrap gap-2">
              <button
                onClick={() => {
                  window.location.href = "mailto:hello@twilanswer.com";
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                Contact Support
              </button>

              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-800 font-medium"
              >
                Log out
              </button>

              <button
                onClick={() => {
                  setOpenDeleteAccountModal(true);
                }}
                className="text-red-500 hover:text-red-600 font-medium"
              >
                Delete Account
              </button>
            </div>
      </div>
    </Dialog>
  );
};

export default ChangeOrgModal;
